/**
 * complete_price_controller.js — ask for a price before completing an
 * unpriced job (Stimulus).
 *
 * Sits on a Complete form that the server marked with
 * data-controller="complete-price" (Jinja filter job_completion_needs_price:
 * completing this job would leave its invoice without a price). Clicking
 * Complete opens #completeJobPriceModal instead of submitting, and the answer
 * is posted as a hidden field on the form:
 *
 *   Add Price & Complete      -> price=<amount>; the job is priced in the
 *                                completion's own transaction and the invoice
 *                                bills it. The auto-send email confirm in
 *                                global-actions.js still runs next.
 *   Complete Without Invoice  -> without_invoice=1; nothing is billed, so the
 *                                email confirm is skipped (data-confirmed).
 *
 * The listener on the form runs before the document-level ones (the email
 * confirm and Turbo), so stopping the first submit keeps both out of it. Once
 * the form carries an answer field every later submit passes through, which
 * is what lets the email confirm resubmit it. The server refuses an
 * unanswered completion of an unpriced job on its own, so a missing modal
 * degrades to a toast, never to an empty invoice.
 *
 * Template: templates/components/modals/confirm_complete_job_modal.html
 * Server:   JobCompletionFormSchema -> JobService.complete_job
 */
import { Controller } from "@hotwired/stimulus";

const MODAL_ID = 'completeJobPriceModal';
const ANSWER_ATTR = 'data-complete-price-answer';

export default class extends Controller {
    connect() {
        this._onSubmit = (e) => this._intercept(e);
        this.element.addEventListener('submit', this._onSubmit);
    }

    disconnect() {
        this.element.removeEventListener('submit', this._onSubmit);
        this._onSubmit = null;
        if (this._release) this._release();
    }

    _intercept(event) {
        // Answered: let this submit (and the email confirm's resubmit) through.
        if (this.element.querySelector(`[${ANSWER_ATTR}]`)) return;
        const modalEl = document.getElementById(MODAL_ID);
        if (!modalEl || typeof bootstrap === 'undefined') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this._open(modalEl);
    }

    /** Show the modal and listen to its two answers until it closes. */
    _open(modalEl) {
        const priceForm = modalEl.querySelector('[data-complete-price-form]');
        const skipBtn = modalEl.querySelector('[data-complete-price-skip]');
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        priceForm.reset();

        const onPrice = (e) => {
            e.preventDefault();
            this._answer(modal, { price: priceForm.elements.price.value });
        };
        const onSkip = () => this._answer(modal, { without_invoice: '1' });
        this._release = () => {
            priceForm.removeEventListener('submit', onPrice);
            skipBtn.removeEventListener('click', onSkip);
            modalEl.removeEventListener('hidden.bs.modal', this._release);
            this._release = null;
        };
        priceForm.addEventListener('submit', onPrice);
        skipBtn.addEventListener('click', onSkip);
        modalEl.addEventListener('hidden.bs.modal', this._release);
        modal.show();
    }

    /** Post the Complete form again, carrying the user's answer. */
    _answer(modal, fields) {
        const form = this.element;
        if (this._release) this._release();
        modal.hide();
        form.querySelectorAll(`[${ANSWER_ATTR}]`).forEach((el) => el.remove());
        Object.entries(fields).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            input.setAttribute(ANSWER_ATTR, '');
            form.appendChild(input);
        });
        // No invoice means no invoice email to confirm.
        if (fields.without_invoice) form.dataset.confirmed = '1';
        form.requestSubmit();
    }
}
